import { useEffect, useState, useRef, useCallback } from 'react';
import websocket_webservice from '../Service/websocket_webservice';

// Generic type for MongoDB Change Stream events
export interface MongoChange<T = any> {
  _id: { _data: string };
  operationType: 'insert' | 'delete' | 'replace' | 'update' | 'drop' | 'rename' | 'dropDatabase' | 'invalidate';
  clusterTime?: string;
  fullDocument?: T;
  documentKey?: { _id: string | { $oid: string } };
  updateDescription?: {
    updatedFields: Record<string, any>;
    removedFields: string[];
  };
}

type ConnectionState = 'connecting' | 'connected' | 'error' | 'closed';

/**
 * Generic Hook for Real-Time MongoDB Updates via WebSocket
 *
 * @param groupName - The group name to join (must match a group in dbchanges.py)
 * @param onData - Callback function to handle the data change
 * @param enabled - Whether to connect (default: true)
 * @returns connection state and reconnect function
 */
export const useRealTimeSubscription = <T>(
  groupName: string,
  onData: (data: MongoChange<T>) => void,
  enabled: boolean = true
) => {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const [connectionState, setConnectionState] = useState<ConnectionState>('closed');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);

  const connect = useCallback(() => {
    if (!enabled || !groupName) return;

    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing socket
    if (socketRef.current) {
      socketRef.current.close();
    }

    // Auto-detect protocol: use WS for HTTP pages, WSS for HTTPS pages
    const isPageSecure = window.location.protocol === 'https:';
    const baseUrl = websocket_webservice.startsWith('wss://') && !isPageSecure
      ? websocket_webservice.replace('wss://', 'ws://')  // Downgrade to WS for HTTP page
      : websocket_webservice;
    const wsUrl = `${baseUrl}ws/${groupName}/`;
    // console.log(`[useRealTimeSubscription] Connecting to ${wsUrl} (page: ${window.location.protocol})`);
    setConnectionState('connecting');
    setErrorDetails(null);

    try {
      const socket = new WebSocket(wsUrl);
      console.log(socket);
      
      socketRef.current = socket;

      socket.onopen = () => {
        // console.log(`[useRealTimeSubscription] Connected to ${groupName}`);
        retryCountRef.current = 0;
        setConnectionState('connected');
        setErrorDetails(null);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onData(data);
        } catch (err) {
          console.error(`[useRealTimeSubscription] Error parsing message for ${groupName}:`, err);
        }
      };

      socket.onerror = (error) => {
        const isWss = wsUrl.startsWith('wss://');
        const isSecureContext = window.isSecureContext;
        const protocol = window.location.protocol;

        let diagnosticMsg = `WebSocket error for ${groupName}`;

        if (isWss && !isSecureContext) {
          diagnosticMsg += ' | WSS requires HTTPS page context';
        }
        if (isWss && protocol === 'http:') {
          diagnosticMsg += ' | Page is HTTP but trying WSS - may cause mixed content issues';
        }

        console.error(`[useRealTimeSubscription] ${diagnosticMsg}:`, error);
        setErrorDetails(diagnosticMsg);
        setConnectionState('error');
      };

      socket.onclose = (event) => {
        let reasonText = event.reason || '';
        if (event.code === 1006) reasonText += ' (Server refused WebSocket upgrade - check Nginx proxy & backend group name)';
        if (event.code === 1001) reasonText += ' (Server going away)';
        if (event.code === 1005) reasonText += ' (No status code provided by server)';

        // console.log(`[useRealTimeSubscription] Disconnected from ${groupName} (code: ${event.code}${reasonText ? ', ' + reasonText : ''})`);
        setConnectionState('closed');

        // Auto-reconnect on abnormal closure (not if cleanly closed), max 5 retries
        if (event.code !== 1000 && event.code !== 1001 && enabled) {
          if (retryCountRef.current < 5) {
            retryCountRef.current += 1;
            // console.log(`[useRealTimeSubscription] Reconnecting in 3s... (attempt ${retryCountRef.current}/5)`);
            reconnectTimeoutRef.current = setTimeout(connect, 3000);
          } else {
            console.warn(`[useRealTimeSubscription] Max retries reached for ${groupName}. Check server WebSocket config.`);
            setErrorDetails(`Server rejected connection after 5 attempts. Code ${event.code}: ${reasonText}`);
            setConnectionState('error');
          }
        }
      };
    } catch (err) {
      console.error(`[useRealTimeSubscription] Failed to create WebSocket:`, err);
      setConnectionState('error');
      setErrorDetails(`Failed to create WebSocket: ${err}`);
    }
  }, [enabled, groupName, onData]);

  const reconnect = useCallback(() => {
    // console.log(`[useRealTimeSubscription] Manual reconnect triggered`);
    connect();
  }, [connect]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [connect]);

  return { connectionState, errorDetails, reconnect };
};
